// Copyright 2016 Sorama B.V.

import { bindingMode, customAttribute, DOM, inject, observable } from "aurelia-framework";
import { NotSupportedError } from "errors/NotSupportedError";

function getNumber(value: string): number {
    if (value == undefined) return NaN;

    const num = parseFloat(value);
    return !isNaN(num) && isFinite(num) ? num : NaN;
}

function bound(num: number, min: number, max: number): number {
    min = isNaN(min) ? Number.NEGATIVE_INFINITY : min;
    max = isNaN(max) ? Number.POSITIVE_INFINITY : max;
    return Math.min(Math.max(num, min), max);
}

@customAttribute("custom-step", bindingMode.twoWay)
@inject(DOM.Element)
export class CustomStep {
    //#region Attribute Value
    public value: string;
    //#endregion

    //#region Fields
    private input: HTMLInputElement;
    private oldNumber: number;
    private isKeyDown: boolean;

    @observable
    private currentStep: number;
    //#endregion

    constructor(input: HTMLInputElement) {
        if (input.type !== "number") {
            throw new NotSupportedError(
                // eslint-disable-next-line quotes
                'custom-step attribute can only be applied to input elements with type="number"'
            );
        }

        this.input = input;
        this.input.onchange = this.valueOnChange;

        throw new Error(
            "This is still buggy and sometimes converts the value into a string when used in conjuction with the NumberValue attribute. Fix that error before using this."
        );
    }

    private currentStepChanged(newValue: number, oldValue: number) {
        this.input.step = newValue.toString();
    }

    private keyDownEvent = (event: KeyboardEvent) => {
        this.isKeyDown = true;
    };

    private inputEvent = (event: Event) => {
        // Don't act if the input was changed via keyboard input
        if (this.isKeyDown) {
            this.oldNumber = getNumber(this.input.value);
            this.currentStep = this.numberToPrecision(this.oldNumber);
            return;
        }

        if (this.input.value === "") return;

        const newNumber = getNumber(this.input.value);
        if (isNaN(newNumber)) return;

        const isStepIncrease = newNumber > this.oldNumber;
        if (isStepIncrease) {
            this.currentStep = this.numberToPrecision(this.oldNumber);
            const newNumberOrder = Math.floor(Math.log10(this.currentStep));

            this.input.value = bound(newNumber, getNumber(this.input.min), getNumber(this.input.max)).toFixed(
                newNumberOrder < 0 ? -newNumberOrder : 0
            ); // Fixes floating-point inaccuracies (see: http://stackoverflow.com/questions/5651039/0-1-strange-result)
        } else {
            // If newNumber is 0, this means we crossed an order boundary going down, so divide the step by 10.
            if (newNumber === 0) {
                this.currentStep /= 10;
            }

            const newNumberOrder = Math.floor(Math.log10(this.currentStep));

            this.input.value = bound(
                this.oldNumber - this.currentStep,
                getNumber(this.input.min),
                getNumber(this.input.max)
            ).toFixed(newNumberOrder < 0 ? -newNumberOrder : 0); // Fixes floating-point inaccuracies (see: http://stackoverflow.com/questions/5651039/0-1-strange-result)
        }

        this.oldNumber = getNumber(this.input.value);
    };

    private keyUpEvent = (event: KeyboardEvent) => {
        this.isKeyDown = false;
    };

    private bind() {
        this.input.addEventListener("keydown", this.keyDownEvent);
        this.input.addEventListener("input", this.inputEvent);
        this.input.addEventListener("keyup", this.keyUpEvent);
    }

    private attached() {
        this.oldNumber = getNumber(this.input.value);

        // Set default step
        this.currentStep = this.numberToPrecision(this.oldNumber);
        if (isNaN(this.currentStep)) {
            this.currentStep = 1;
        }
    }

    private unbind() {
        this.input.removeEventListener("keydown", this.keyDownEvent);
        this.input.removeEventListener("input", this.inputEvent);
        this.input.removeEventListener("keyup", this.keyUpEvent);
    }

    private numberToPrecision(value: number): number {
        const numberAsString = value.toLocaleString("en-US").split(".");
        const numberOfDigits = numberAsString.length == 1 ? 0 : numberAsString[1].length;
        return Math.pow(10, -1 * numberOfDigits);
    }

    private valueOnChange = () => {
        if (this.input) {
            this.currentStep = this.numberToPrecision(Number.parseFloat(this.input.value));
        }
    };
}
